import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  AbstractControl,
} from "@angular/forms";
import { HttpParams } from "@angular/common/http";
import { checkVAT, countries } from "jsvat";
import { getCurrentAccount } from "../../accounts/accounts.reducer";
import { BillingSettings } from "../../settings/settings.interface";
import { Store, ActionsSubject } from "@ngrx/store";
import { ofType } from "@ngrx/effects";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, takeUntil } from "rxjs/operators";
import type { Observable } from "rxjs";
import { ToastrService } from "../../shared/toastr.service";
import { TranslateService } from "@ngx-translate/core";
import { MatDialog } from "@angular/material/dialog";
import { BillingSettingsService } from "./billing-settings.service";
import { ConfirmationDialogComponent } from "../../shared/confirmation-dialog/confirmation-dialog.component";
import { DateFormatService } from "../../shared/date-format.service";
import { FreemiumPlanDialogComponent } from "./freemium-plan-dialog/freemium-plan-dialog.component";
import { YearlyLicensePurchaseDialogComponent } from "./yearly-license-purchase-dialog/yearly-license-purchase-dialog.component";
import { MonthlyLicensePurchaseDialogComponent } from "./monthly-license-purchase-dialog/monthly-license-purchase-dialog.component";
import { UpfrontLicensePurchaseDialogComponent } from "./upfront-license-purchase-dialog/upfront-license-purchase-dialog.component";
import { UpfrontLicenseChangeDialogComponent } from "./upfront-license-change-dialog/upfront-license-change-dialog.component";
import { Router } from "@angular/router";
import {
  GetBillingSettingsAction,
  ChangeBillingSettingsAction,
  CHANGE_BILLING_SETTINGS_SUCCESS,
  CHANGE_BILLING_SETTINGS_FAILURE,
} from "../../settings/settings.actions";
import { getBillingSettingsSelector } from "../../settings/settings.reducer";
import { AuthService } from "../../auth/auth.service";
import { environment } from "../../../environments/environment";
import { ShowOnSubmitAttemptErrorStateMatcher } from "../../shared/error-state-matcher";
import { ConfirmationDialogParams } from "../../shared/confirmation-dialog/confirmation-dialog.interface";
import {
  BillingStats,
  LicensePurchaseDialogResult,
  MonthlyLicensePurchaseDialogData,
  UpfrontLicenseChangeDialogData,
  UpfrontLicenseChangeDialogResult,
  UpfrontLicensePurchaseDialogData,
  YearlyLicensePurchaseDialogData,
} from "../billing.interface";

@Component({
  selector: "app-billing-settings",
  templateUrl: "./billing-settings.component.html",
  styleUrls: ["./billing-settings.component.scss"],
  standalone: false,
})
export class BillingSettingsComponent implements OnInit, OnDestroy {
  currentDomain: string | null = null;
  stats: BillingStats | null = null;
  billingSettings: BillingSettings | null = null;
  displayedColumns: string[] = ["date", "numberOfEmployees", "price"];
  displayedColumnsWithAction: string[] = [
    "date",
    "numberOfEmployees",
    "price",
    "action",
  ];
  plans = ["basic", "premium"];
  availablePlans: string[] = [];
  isPlanSelectDisabled = false;
  currencies = ["USD", "EUR", "PLN"];
  paymentMethod = "";
  isProcessingPayment = false;
  savingSettings = false;
  savingPaymentMethod = false;
  hasChanges = false;
  hasPaymentMethodChanges = false;
  loading = true;

  isLoadingStats = false;
  isRunningManualReport = false;
  isRunningMonthlyReport = false;
  isRunningYearlyReport = false;

  isDevelopmentEnvironment = !environment.production;
  isRunningStripeCron = false;
  cancellingPlanChange = false;

  errorStateMatcher = new ShowOnSubmitAttemptErrorStateMatcher();

  form = new UntypedFormGroup({
    planName: new UntypedFormControl(""),
    price: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.pattern(/^\d+(\.\d{1,2})?$/),
    ]),
    basePrice: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.pattern(/^\d+(\.\d{1,2})?$/),
    ]),
    currency: new UntypedFormControl(""),
    planId: new UntypedFormControl({ value: "", disabled: true }),
    subscriptionStartDate: new UntypedFormControl({
      value: "",
      disabled: true,
    }),
    lastInvoiceUserCount: new UntypedFormControl({ value: "", disabled: true }),
    currentUserCount: new UntypedFormControl({ value: "", disabled: true }),
    invoiceUserCount: new UntypedFormControl({ value: "", disabled: true }),
    margin: new UntypedFormControl({ value: "", disabled: true }),
    discount: new UntypedFormControl({ value: "", disabled: true }, [
      Validators.min(0),
      Validators.max(100),
    ]),
    discountValidUntil: new UntypedFormControl({ value: "", disabled: true }),
    subscriptionPeriod: new UntypedFormControl(""),
    contactName: new UntypedFormControl(""),
    contactPhone: new UntypedFormControl(""),
    contactEmail: new UntypedFormControl("", [
      (c: AbstractControl) => Validators.email(c),
    ]),
    companyName: new UntypedFormControl(""),
    country: new UntypedFormControl(""),
    street: new UntypedFormControl(""),
    streetNumber: new UntypedFormControl(""),
    city: new UntypedFormControl(""),
    postalCode: new UntypedFormControl(""),
    state: new UntypedFormControl(""),
    vatNumber: new UntypedFormControl("", [
      (c: AbstractControl) => this.vatNumberValidator(c as UntypedFormControl),
    ]),
    vatNotApplicable: new UntypedFormControl(false),
    vatRate: new UntypedFormControl({ value: 0, disabled: true }),
  });

  private destroy$ = new Subject<void>();
  private initialFormValue: string | null = null;
  private initialPaymentMethod: string | null = null;
  private formListenersSetup = false;
  private isLoadingData = false;

  private vatNumberValidator(control: UntypedFormControl) {
    const vatNotApplicable = this.form?.get("vatNotApplicable")
      ?.value as boolean;

    if (vatNotApplicable) {
      return null;
    }

    const vatNumber = (control.value as string)?.trim();
    const countryCode = this.form?.get("country")?.value as string;

    if (countryCode !== "PL") {
      return null;
    }

    if (!vatNumber || !countryCode) {
      return null;
    }

    const fullVatNumber = vatNumber.startsWith(countryCode)
      ? vatNumber
      : `${countryCode}${vatNumber}`;
    const isValid = checkVAT(fullVatNumber, countries).isValid;

    return isValid
      ? null
      : {
          invalidVAT: {
            message: this.translate.instant(
              "settings.billing.invalidVatNumber",
            ) as string,
          },
        };
  }

  constructor(
    private billingSettingsService: BillingSettingsService,
    private store: Store,
    private actions$: ActionsSubject,
    public auth: AuthService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private dialog: MatDialog,
    public dateFormatService: DateFormatService,
    private router: Router,
  ) {
    this.setupActionListeners();
  }

  ngOnInit(): void {
    this.setupSubscriptions();
    this.loadInitialData();
    this.updateFormFieldPermissions();
    this.form
      .get("country")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((alpha2: string) => this.onCountryChange(alpha2));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSubmit(): void {
    if (this.loading) {
      return;
    }
    this.errorStateMatcher.submitAttempted = true;
    if (this.form.valid) {
      const formValue = this.prepareFormValue() as BillingSettings;

      const isPlanChanging =
        formValue.planName &&
        this.billingSettings &&
        formValue.planName.toLowerCase() !==
          this.billingSettings.planName?.toLowerCase();

      if (isPlanChanging) {
        this.showPlanChangeConfirmationDialog(formValue);
      } else {
        this.saveBillingSettings(formValue);
      }
    }
  }

  private showPlanChangeConfirmationDialog(formValue: BillingSettings): void {
    const nextMonth = new Date();
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    nextMonth.setDate(1);
    const nextMonthDate = this.dateFormatService.formatDate(nextMonth);

    const dialogData: {
      body: string;
      confirmButton: string;
      additionalInfo?: string;
    } = {
      body: this.translate.instant(
        "subscription.changePlanConfirmation.message",
        { planName: formValue.planName },
      ) as string,
      confirmButton: this.translate.instant(
        "subscription.changePlanConfirmation.confirm",
      ) as string,
    };

    if (
      this.billingSettings &&
      this.billingSettings.planId !== "free" &&
      this.billingSettings.planId !== "freemium"
    ) {
      dialogData.additionalInfo = this.translate.instant(
        "subscription.changePlanConfirmation.effectiveDate",
        { date: nextMonthDate },
      ) as string;
    }

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.saveBillingSettings(formValue);
      }
    });
  }

  private saveBillingSettings(formValue: BillingSettings): void {
    this.savingSettings = true;
    this.store.dispatch(new ChangeBillingSettingsAction(formValue));

    setTimeout(() => {
      this.savingSettings = false;
      this.hasChanges = false;
      this.initialFormValue = JSON.stringify(this.form.getRawValue());
    }, 1000);
  }

  savePaymentMethod(): void {
    this.savingPaymentMethod = true;
    this.billingSettingsService
      .updatePaymentMethod(this.paymentMethod)
      .subscribe({
        next: () => {
          this.toastr.success(
            this.translate.instant(
              "settings.billing.paymentMethodUpdateSuccess",
            ) as string,
          );
          this.savingPaymentMethod = false;
          this.hasPaymentMethodChanges = false;
          this.initialPaymentMethod = this.paymentMethod;
        },
        error: () => {
          this.savingPaymentMethod = false;
        },
      });
  }

  refreshBillingForm(): void {
    if (this.isUpfrontPaymentWithoutSubscription()) {
      const defaultEmployeeCount =
        this.billingSettings?.totalCurrentUserCount ??
        (this.form.get("currentUserCount")?.value as number | undefined) ??
        this.billingSettings?.currentUserCount ??
        this.billingSettings?.upfrontPayment?.licenseCount ??
        0;
      this.upfrontLicenseCount = Math.max(defaultEmployeeCount, 10);
      this.calculateUpfrontPrice();
    }
    this.store.dispatch(new GetBillingSettingsAction());
  }

  refreshStats(): void {
    if (!this.currentDomain || !this.billingSettings) {
      return;
    }
    this.initializeStatsView({
      ...this.billingSettings,
      subscriptionPeriod: (this.form.get("subscriptionPeriod")?.value ??
        this.billingSettings
          .subscriptionPeriod) as BillingSettings["subscriptionPeriod"],
    });
  }

  onCountryChange(alpha2: string): void {
    const isPoland = alpha2 === "PL";
    this.form.get("vatRate")?.setValue(isPoland ? 23 : 0);

    if (this.auth.isSuperAdmin || isPoland) {
      this.form.get("vatRate")?.enable();
    } else {
      this.form.get("vatRate")?.disable();
    }

    this.form.get("vatNumber")?.updateValueAndValidity();
  }

  onVatNotApplicableChange(checked: boolean): void {
    const vatNumberControl = this.form.get("vatNumber");
    if (checked) {
      vatNumberControl?.setValue("");
      vatNumberControl?.clearValidators();
      vatNumberControl?.disable();
    } else {
      vatNumberControl?.enable();
      vatNumberControl?.setValidators([
        (c) => this.vatNumberValidator(c as UntypedFormControl),
      ]);
    }
    vatNumberControl?.updateValueAndValidity();
  }

  updatePrice(): void {
    if (this.isLoadingData) {
      return;
    }

    const plan = this.form.get("planName")?.value as string;
    const period = this.form.get("subscriptionPeriod")?.value as string;
    const currency = (this.form.get("currency")?.value as string) || "USD";

    if (plan && period) {
      const isPLN = currency.toUpperCase() === "PLN";
      const priceMap: {
        [p: string]: { [per: string]: { usd: number; pln: number } };
      } = {
        basic: {
          monthly: { usd: 0.99, pln: 3.99 },
          yearly: { usd: 0.89, pln: 3.59 },
        },
        premium: {
          monthly: { usd: 1.99, pln: 7.99 },
          yearly: { usd: 1.79, pln: 7.19 },
        },
      };

      const p = plan.toLowerCase();
      const per = period.toLowerCase();
      const priceData = priceMap[p]?.[per];

      if (priceData) {
        const basePrice = isPLN ? priceData.pln : priceData.usd;
        this.form.get("basePrice")?.setValue(basePrice, { emitEvent: false });
        this.calculatePriceWithDiscount();
      }
    }
  }

  calculatePriceWithDiscount(): void {
    if (this.isLoadingData) {
      return;
    }

    const basePrice = this.form.get("basePrice")?.value as number | string;
    const discount = this.form.get("discount")?.value as number;
    const discountValidUntil = this.form.get("discountValidUntil")?.value as
      string | Date;

    if (basePrice === null || basePrice === undefined || basePrice === "") {
      return;
    }

    const numBasePrice = parseFloat(String(basePrice));
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    let finalPrice = numBasePrice;

    if (discount && discount > 0 && discount <= 100) {
      if (discountValidUntil) {
        const validUntil = new Date(discountValidUntil);
        validUntil.setHours(0, 0, 0, 0);

        if (validUntil >= now) {
          finalPrice = numBasePrice * (1 - discount / 100);
        }
      } else {
        finalPrice = numBasePrice * (1 - discount / 100);
      }
    }

    this.form.get("price")?.setValue(parseFloat(finalPrice.toFixed(2)), {
      emitEvent: false,
    });
    if (this.isUpfrontPaymentWithoutSubscription()) {
      this.calculateUpfrontPrice();
    }
  }

  onPaymentMethodChange(newValue: string): void {
    this.paymentMethod = newValue;
    this.checkForPaymentMethodChanges();
  }

  runManualBillingReport(): void {
    this.isRunningManualReport = true;
    this.billingSettingsService.runManualBillingReport().subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(
            "settings.billing.manualReportSuccess",
          ) as string,
        );
        this.refreshStats();
        this.refreshBillingForm();
        this.isRunningManualReport = false;
      },
      error: () => {
        this.toastr.error(
          this.translate.instant(
            "settings.billing.manualReportError",
          ) as string,
        );
        this.isRunningManualReport = false;
      },
    });
  }

  runManualMonthlyBillingReport(): void {
    this.isRunningMonthlyReport = true;
    this.billingSettingsService.runManualBillingReport("monthly").subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(
            "settings.billing.monthlyReportSuccess",
          ) as string,
        );
        this.refreshStats();
        this.refreshBillingForm();
        this.isRunningMonthlyReport = false;
      },
      error: () => {
        this.toastr.error(
          this.translate.instant(
            "settings.billing.monthlyReportError",
          ) as string,
        );
        this.isRunningMonthlyReport = false;
      },
    });
  }

  runManualYearlyBillingReport(): void {
    this.isRunningYearlyReport = true;
    this.billingSettingsService.runManualBillingReport("yearly").subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(
            "settings.billing.yearlyReportSuccess",
          ) as string,
        );
        this.refreshStats();
        this.refreshBillingForm();
        this.isRunningYearlyReport = false;
      },
      error: () => {
        this.toastr.error(
          this.translate.instant(
            "settings.billing.yearlyReportError",
          ) as string,
        );
        this.isRunningYearlyReport = false;
      },
    });
  }

  private updateLocalBillingSettings(): void {
    if (!this.billingSettings) {
      return;
    }

    const formValue = this.form.value as BillingSettings;
    this.billingSettings = {
      ...this.billingSettings,
      planName: formValue.planName || this.billingSettings.planName,
      price: formValue.price ?? this.billingSettings.price,
      basePrice: formValue.basePrice ?? this.billingSettings.basePrice,
      currency: formValue.currency || this.billingSettings.currency,
      subscriptionPeriod:
        formValue.subscriptionPeriod || this.billingSettings.subscriptionPeriod,
      vatRate: formValue.vatRate ?? this.billingSettings.vatRate,
    };
  }

  openPaymentModal(): void {
    this.updateLocalBillingSettings();

    if (this.isUpfrontPaymentPastDue()) {
      const returnUrl = `${window.location.origin}/settings/billing`;
      this.saveBillingSettingsSilent().subscribe({
        next: () => {
          this.billingSettingsService
            .createUpfrontPortalSession(returnUrl)
            .subscribe({
              next: (response) => {
                window.location.href = response.url;
              },
              error: () => {
                this.toastr.error(
                  this.translate.instant(
                    "settings.billing.paymentError",
                  ) as string,
                );
              },
            });
        },
        error: () => {
          this.toastr.error(
            this.translate.instant("settings.billing.updateError") as string,
          );
        },
      });
      return;
    }

    if (this.paymentMethod !== "card") {
      this.toastr.info(
        this.translate.instant("settings.billing.invoiceSelected") as string,
      );
      return;
    }

    if (this.billingSettings?.billingMode === "upfront") {
      if (this.billingSettings?.upfrontPayment?.subscriptionId) {
        const returnUrl = `${window.location.origin}/settings/billing`;
        this.saveBillingSettingsSilent().subscribe({
          next: () => {
            this.billingSettingsService
              .createUpfrontPortalSession(returnUrl)
              .subscribe({
                next: (response) => {
                  window.location.href = response.url;
                },
                error: () => {
                  this.toastr.error(
                    this.translate.instant(
                      "settings.billing.paymentError",
                    ) as string,
                  );
                },
              });
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("settings.billing.updateError") as string,
            );
          },
        });
        return;
      }
      if (this.isUpfrontPaymentWithoutSubscription()) {
        if (this.upfrontHasLicenseCountError) {
          this.toastr.error(
            this.translate.instant(
              "settings.billing.upfrontLicensePurchase.minLicenseError",
            ) as string,
          );
          return;
        }
        const effectiveEmployeeCount = Math.max(
          this.upfrontLicenseCount,
          this.upfrontMinLicenseCount,
        );
        this.saveBillingSettingsSilent().subscribe({
          next: () => {
            this.billingSettingsService.validateBillingInfo().subscribe({
              next: (resp) => {
                if (resp.success) {
                  this.proceedToUpfrontCheckout(effectiveEmployeeCount);
                } else {
                  this.handleValidationError(resp.missingFields || []);
                }
              },
              error: () => {
                this.toastr.error(
                  this.translate.instant(
                    "settings.billing.validationError",
                  ) as string,
                );
                this.isProcessingPayment = false;
              },
            });
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("settings.billing.updateError") as string,
            );
            this.isProcessingPayment = false;
          },
        });
        return;
      }
      this.showUpfrontLicensePurchaseDialog();
      return;
    }

    this.isProcessingPayment = true;
    this.errorStateMatcher.submitAttempted = true;
    if (!this.form.valid) {
      this.isProcessingPayment = false;
      return;
    }

    this.saveBillingSettingsSilent(
      new HttpParams().set("stripeButtonClicked", "true"),
    ).subscribe({
      next: () => this.validateAndProceedToPayment(),
      error: () => {
        this.toastr.error(
          this.translate.instant("settings.billing.updateError") as string,
        );
        this.isProcessingPayment = false;
      },
    });
  }

  private saveBillingSettingsSilent(
    params?: HttpParams,
  ): Observable<BillingSettings> {
    return this.billingSettingsService.update(
      this.prepareFormValue() as BillingSettings,
      params,
    );
  }

  private setupActionListeners(): void {
    this.actions$
      .pipe(
        ofType(
          CHANGE_BILLING_SETTINGS_SUCCESS,
          CHANGE_BILLING_SETTINGS_FAILURE,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((action: { type: string; payload?: unknown }) => {
        this.savingSettings = false;
        if (action.type === CHANGE_BILLING_SETTINGS_SUCCESS) {
          this.refreshBillingForm();
        }
      });
  }

  private setupSubscriptions(): void {
    this.store
      .select(getCurrentAccount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((domain) => {
        this.currentDomain = domain;
        if (domain && this.billingSettings) {
          this.handleBillingSettingsUpdate(this.billingSettings);
        }
      });

    this.store
      .select(getBillingSettingsSelector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((billingSettings: BillingSettings) => {
        if (billingSettings) {
          if (this.currentDomain) {
            this.handleBillingSettingsUpdate(billingSettings);
          } else {
            this.billingSettings = billingSettings;
          }
        }
      });
  }

  private setupFormListeners(): void {
    if (this.formListenersSetup) {
      return;
    }

    this.form
      .get("subscriptionPeriod")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updatePrice();
        this.refreshStats();
        if (this.isUpfrontPaymentWithoutSubscription()) {
          this.calculateUpfrontPrice();
        }
      });

    this.form
      .get("planName")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePrice());

    this.form
      .get("currency")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePrice());

    this.form
      .get("basePrice")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calculatePriceWithDiscount());

    this.form
      .get("discount")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calculatePriceWithDiscount());

    this.form
      .get("discountValidUntil")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calculatePriceWithDiscount());

    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkForChanges());

    this.formListenersSetup = true;
  }

  private loadInitialData(): void {
    this.store.dispatch(new GetBillingSettingsAction());
  }

  private handleBillingSettingsUpdate(billingSettings: BillingSettings): void {
    this.billingSettings = billingSettings;
    this.paymentMethod = billingSettings.paymentMethod || "";
    this.initialPaymentMethod = this.paymentMethod;

    this.isLoadingData = true;
    this.fillForm(billingSettings);
    this.setupFormListeners();
    this.updatePlanSelectPermissions();
    this.initializeStatsView(billingSettings, true);
    if (this.isUpfrontPaymentWithoutSubscription()) {
      const defaultEmployeeCount =
        billingSettings.totalCurrentUserCount ??
        (this.form.get("currentUserCount")?.value as number | undefined) ??
        billingSettings.currentUserCount ??
        billingSettings.upfrontPayment?.licenseCount ??
        0;
      this.upfrontLicenseCount = Math.max(defaultEmployeeCount, 10);
      this.calculateUpfrontPrice();
    }
    this.isLoadingData = false;
  }

  private fillForm(billingSettings: BillingSettings): void {
    this.form.patchValue(
      {
        planName: billingSettings.planName,
        price: billingSettings.price,
        basePrice: billingSettings.basePrice || billingSettings.price,
        currency: billingSettings.currency,
        planId: billingSettings.planId,
        subscriptionStartDate: new Date(billingSettings.subscriptionStartDate),
        lastInvoiceUserCount: billingSettings.lastInvoiceUserCount,
        currentUserCount: billingSettings.currentUserCount,
        invoiceUserCount: billingSettings.invoiceUserCount || "",
        margin: billingSettings.margin || "",
        discount: billingSettings.discount || "",
        discountValidUntil: billingSettings.discountValidUntil
          ? new Date(billingSettings.discountValidUntil)
          : "",
        subscriptionPeriod: billingSettings.subscriptionPeriod,
        contactName: billingSettings.contactName || "",
        contactPhone: billingSettings.contactPhone || "",
        contactEmail: billingSettings.contactEmail || "",
        companyName: billingSettings.companyName || "",
        country: billingSettings.country || "",
        street: billingSettings.street || "",
        streetNumber: billingSettings.streetNumber || "",
        city: billingSettings.city || "",
        postalCode: billingSettings.postalCode || "",
        state: billingSettings.state || "",
        vatNumber: billingSettings.vatNumber || "",
        vatNotApplicable: billingSettings.vatNotApplicable || false,
        vatRate: billingSettings.vatRate,
      },
      { emitEvent: false },
    );

    const isPoland = billingSettings.country === "PL";
    if (this.auth.isSuperAdmin || isPoland) {
      this.form.get("vatRate")?.enable();
    } else {
      this.form.get("vatRate")?.disable();
    }

    if (billingSettings.vatNotApplicable) {
      const vatNumberControl = this.form.get("vatNumber");
      vatNumberControl?.setValue("");
      vatNumberControl?.clearValidators();
      vatNumberControl?.disable();
      vatNumberControl?.updateValueAndValidity();
    }

    this.initialFormValue = JSON.stringify(this.form.getRawValue());
  }

  private initializeStatsView(
    billingSettings: BillingSettings,
    gatePageLoading = false,
  ): void {
    const period = billingSettings.subscriptionPeriod;
    const domain = this.currentDomain;
    this.isLoadingStats = true;
    if (gatePageLoading) {
      this.loading = true;
    }
    this.stats = {
      monthly: [],
      yearly: [],
      aggregated: [],
      subdomains: {},
      resellerClients: {},
    };

    if (!domain) {
      this.isLoadingStats = false;
      if (gatePageLoading) {
        this.loading = false;
      }
      return;
    }

    const primary$ =
      period === "yearly"
        ? this.billingSettingsService
            .getYearlyStats(domain)
            .pipe(catchError(() => of([])))
        : this.billingSettingsService
            .getMonthlyStats(domain)
            .pipe(catchError(() => of([])));

    const subdomainDomains = billingSettings.subdomains ?? [];
    const resellerDomains = billingSettings.resellerClients ?? [];

    forkJoin({
      primary: primary$,
      aggregated: this.billingSettingsService
        .getAggregatedMonthlyStats(domain)
        .pipe(catchError(() => of([]))),
      subdomains: subdomainDomains.length
        ? forkJoin(
            Object.fromEntries(
              subdomainDomains.map((sd) => [
                sd.domain,
                (period === "yearly"
                  ? this.billingSettingsService.getYearlyStats(sd.domain)
                  : this.billingSettingsService.getMonthlyStats(sd.domain)
                ).pipe(catchError(() => of([]))),
              ]),
            ) as Record<
              string,
              ReturnType<BillingSettingsService["getMonthlyStats"]>
            >,
          )
        : of<BillingStats["subdomains"]>({}),
      resellerClients: resellerDomains.length
        ? forkJoin(
            Object.fromEntries(
              resellerDomains.map((client) => [
                client.domain,
                (period === "yearly"
                  ? this.billingSettingsService.getYearlyStats(client.domain)
                  : this.billingSettingsService.getMonthlyStats(client.domain)
                ).pipe(catchError(() => of([]))),
              ]),
            ) as Record<
              string,
              ReturnType<BillingSettingsService["getMonthlyStats"]>
            >,
          )
        : of<BillingStats["resellerClients"]>({}),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ primary, aggregated, subdomains, resellerClients }) => {
          if (period === "yearly") {
            this.stats.yearly = primary;
          } else {
            this.stats.monthly = primary;
          }
          this.stats.aggregated = aggregated;
          this.stats.subdomains = subdomains;
          this.stats.resellerClients = resellerClients;
          this.isLoadingStats = false;
          if (gatePageLoading) {
            this.loading = false;
          }
        },
        error: () => {
          this.isLoadingStats = false;
          if (gatePageLoading) {
            this.loading = false;
          }
        },
      });
  }

  private prepareFormValue(): Partial<BillingSettings> &
    Record<string, unknown> {
    const formValue = this.form.getRawValue() as Record<string, unknown>;
    if (formValue.subscriptionStartDate instanceof Date) {
      (formValue as Record<string, string>).subscriptionStartDate =
        formValue.subscriptionStartDate.toISOString();
    } else {
      (formValue as Record<string, string>).subscriptionStartDate = (
        formValue.subscriptionStartDate as { format: (fmt: string) => string }
      ).format("YYYY-MM-DD");
    }

    if (this.form.get("vatNotApplicable")?.value) {
      (formValue as { vatNumber: string | null }).vatNumber = null;
    }

    const textFieldsToTrim = [
      "planName",
      "currency",
      "planId",
      "subscriptionPeriod",
      "contactName",
      "contactPhone",
      "contactEmail",
      "companyName",
      "country",
      "street",
      "streetNumber",
      "city",
      "postalCode",
      "state",
      "vatNumber",
    ];

    textFieldsToTrim.forEach((field) => {
      const value = formValue[field] as string | null | undefined;
      (formValue as Record<string, string | null>)[field] = value || null;
    });

    (formValue as { paymentMethod: string | null }).paymentMethod =
      this.paymentMethod || null;

    const basePriceValue = this.form.get("basePrice")?.value as
      string | number | undefined;
    (formValue as { basePrice?: number }).basePrice =
      basePriceValue && basePriceValue !== ""
        ? Number(basePriceValue)
        : undefined;

    const invoiceUserCountValue = this.form.get("invoiceUserCount")?.value as
      string | number | undefined;
    (formValue as { invoiceUserCount?: number }).invoiceUserCount =
      invoiceUserCountValue && invoiceUserCountValue !== ""
        ? Number(invoiceUserCountValue)
        : undefined;

    const marginValue = this.form.get("margin")?.value as
      string | number | undefined;
    (formValue as { margin?: number }).margin =
      marginValue && marginValue !== "" ? Number(marginValue) : undefined;

    const discountValue = this.form.get("discount")?.value as
      string | number | undefined;
    (formValue as { discount?: number }).discount =
      discountValue && discountValue !== "" ? Number(discountValue) : undefined;

    const discountValidUntilValue = this.form.get("discountValidUntil")
      ?.value as Date | string | undefined;
    if (discountValidUntilValue && discountValidUntilValue !== "") {
      if (discountValidUntilValue instanceof Date) {
        (
          formValue as { discountValidUntil: string | null }
        ).discountValidUntil = discountValidUntilValue.toISOString();
      } else {
        (
          formValue as { discountValidUntil: string | null }
        ).discountValidUntil = discountValidUntilValue;
      }
    } else {
      (formValue as { discountValidUntil: string | null }).discountValidUntil =
        null;
    }

    if (this.billingSettings?.planId === "freemium") {
      (formValue as { planName: string }).planName =
        this.billingSettings.planName ?? "";
    }

    return formValue;
  }

  private validateAndProceedToPayment(): void {
    const subscriptionPeriod = this.form.get("subscriptionPeriod")?.value as
      string | undefined;
    const isYearly = subscriptionPeriod === "yearly";
    const isFreemiumPlan = this.billingSettings?.planId === "freemium";

    if (isFreemiumPlan) {
      this.billingSettingsService.validateBillingInfo().subscribe({
        next: (resp) => {
          if (resp.success) {
            this.showFreemiumPlanDialog();
            this.isProcessingPayment = false;
          } else {
            this.handleValidationError(resp.missingFields || []);
          }
        },
        error: () => {
          this.toastr.error(
            this.translate.instant(
              "settings.billing.validationError",
            ) as string,
          );
          this.isProcessingPayment = false;
        },
      });
      return;
    }

    if (isYearly && this.stats?.yearly && this.stats.yearly.length === 0) {
      this.showYearlyLicensePurchaseDialog();
      return;
    }

    if (!isYearly && this.stats?.monthly && this.stats.monthly.length === 0) {
      this.showMonthlyLicensePurchaseDialog();
      return;
    }

    this.billingSettingsService.validateBillingInfo().subscribe({
      next: (resp) => {
        if (resp.success) {
          if (resp.isFreemiumPlan) {
            this.showFreemiumPlanDialog();
            this.isProcessingPayment = false;
          } else {
            this.proceedToCheckout();
          }
        } else {
          this.handleValidationError(resp.missingFields || []);
        }
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("settings.billing.validationError") as string,
        );
        this.isProcessingPayment = false;
      },
    });
  }

  private showYearlyLicensePurchaseDialog(): void {
    const defaultEmployeeCount =
      (this.form.get("currentUserCount")?.value as number | undefined) ||
      this.billingSettings?.currentUserCount ||
      0;
    const latestYearlyStat =
      this.stats?.yearly && this.stats.yearly.length > 0
        ? this.stats.yearly.reduce((latest, current) =>
            new Date(current.date) > new Date(latest.date) ? current : latest,
          )
        : null;

    const dialogData: YearlyLicensePurchaseDialogData = {
      defaultEmployeeCount: defaultEmployeeCount,
      currency:
        (this.form.get("currency")?.value as string | undefined) ||
        this.billingSettings?.currency ||
        "USD",
      planName:
        (this.form.get("planName")?.value as string | undefined) ||
        this.billingSettings?.planName ||
        "basic",
      price:
        (this.form.get("price")?.value as number | undefined) ||
        this.billingSettings?.price ||
        0,
      lastPaymentDate: latestYearlyStat
        ? new Date(latestYearlyStat.date)
        : undefined,
      billingMode: this.billingSettings?.billingMode,
    };

    const dialogRef = this.dialog.open(YearlyLicensePurchaseDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: LicensePurchaseDialogResult | undefined) => {
        if (
          result &&
          result.proceed &&
          result.numberOfEmployees !== undefined
        ) {
          const isAdditional = this.isYearlyPlanWithExistingPayment();
          if (isAdditional) {
            this.billingSettingsService
              .purchaseAdditionalLicenses(result.numberOfEmployees)
              .subscribe({
                next: (stripe) => {
                  window.location.href = stripe.url;
                },
                error: () => {
                  this.toastr.error(
                    this.translate.instant(
                      "settings.billing.paymentError",
                    ) as string,
                  );
                  this.isProcessingPayment = false;
                },
              });
          } else {
            this.proceedToCheckout(result.numberOfEmployees);
          }
        } else {
          this.isProcessingPayment = false;
        }
      });
  }

  private showMonthlyLicensePurchaseDialog(): void {
    const defaultEmployeeCount =
      (this.form.get("currentUserCount")?.value as number | undefined) ||
      this.billingSettings?.currentUserCount ||
      0;

    const dialogData: MonthlyLicensePurchaseDialogData = {
      defaultEmployeeCount: defaultEmployeeCount,
      currency:
        (this.form.get("currency")?.value as string | undefined) ||
        this.billingSettings?.currency ||
        "USD",
      planName:
        (this.form.get("planName")?.value as string | undefined) ||
        this.billingSettings?.planName ||
        "basic",
      price:
        (this.form.get("price")?.value as number | undefined) ||
        this.billingSettings?.price ||
        0,
      subscriptionStartDate: this.billingSettings?.subscriptionStartDate
        ? new Date(this.billingSettings.subscriptionStartDate)
        : undefined,
      billingMode: this.billingSettings?.billingMode,
    };

    const dialogRef = this.dialog.open(MonthlyLicensePurchaseDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: LicensePurchaseDialogResult | undefined) => {
        if (
          result &&
          result.proceed &&
          result.numberOfEmployees !== undefined
        ) {
          this.billingSettingsService.validateBillingInfo().subscribe({
            next: (resp) => {
              if (resp.success) {
                if (resp.isFreemiumPlan) {
                  this.showFreemiumPlanDialog();
                  this.isProcessingPayment = false;
                } else {
                  this.proceedToCheckout(result.numberOfEmployees);
                }
              } else {
                this.handleValidationError(resp.missingFields || []);
              }
            },
            error: () => {
              this.toastr.error(
                this.translate.instant(
                  "settings.billing.validationError",
                ) as string,
              );
              this.isProcessingPayment = false;
            },
          });
        } else {
          this.isProcessingPayment = false;
        }
      });
  }

  private showFreemiumPlanDialog(): void {
    this.dialog.open(FreemiumPlanDialogComponent, {
      width: "500px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
    });
  }

  private proceedToCheckout(numberOfEmployees?: number): void {
    const flowType =
      this.form.get("subscriptionPeriod")?.value === "yearly"
        ? "yearly"
        : "billing";
    this.saveBillingSettingsSilent().subscribe({
      next: () => {
        this.billingSettingsService
          .createCheckoutSession(flowType, numberOfEmployees)
          .subscribe({
            next: (stripe) => (window.location.href = stripe.url),
            error: () => {
              this.toastr.error(
                this.translate.instant(
                  "settings.billing.paymentError",
                ) as string,
              );
              this.isProcessingPayment = false;
            },
          });
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("settings.billing.updateError") as string,
        );
        this.isProcessingPayment = false;
      },
    });
  }

  private handleValidationError(missingFields: string[]): void {
    this.toastr.warning(
      this.translate.instant("settings.billing.fillRequiredFields") as string,
    );
    setTimeout(() => {
      document.getElementById("billing-info-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 200);
    this.highlightMissingFields(missingFields);
    this.isProcessingPayment = false;
  }

  private highlightMissingFields(missingFields: string[]): void {
    missingFields.forEach((field) => {
      const ctrl = this.form.get(field);
      if (ctrl) {
        ctrl.markAsTouched();
        ctrl.setErrors({ required: true });
        const el = document.querySelector(
          `mat-form-field[formControlName="${field}"]`,
        );
        el?.classList.add("mat-form-field-error");
      }
    });
  }

  private checkForChanges(): void {
    if (this.initialFormValue) {
      this.hasChanges =
        this.initialFormValue !== JSON.stringify(this.form.getRawValue());
    }
  }

  private checkForPaymentMethodChanges(): void {
    if (this.initialPaymentMethod !== null) {
      this.hasPaymentMethodChanges =
        this.initialPaymentMethod !== this.paymentMethod;
    }
  }

  private updateFormFieldPermissions(): void {
    if (this.auth.isSuperAdmin) {
      this.form.get("subscriptionStartDate")?.enable();
      this.form.get("basePrice")?.enable();
      this.form.get("invoiceUserCount")?.enable();
      this.form.get("margin")?.enable();
      this.form.get("discount")?.enable();
      this.form.get("discountValidUntil")?.enable();
    } else {
      this.form.get("subscriptionStartDate")?.disable();
      this.form.get("basePrice")?.disable();
      this.form.get("invoiceUserCount")?.disable();
      this.form.get("margin")?.disable();
      this.form.get("discount")?.disable();
      this.form.get("discountValidUntil")?.disable();
    }
  }

  private updatePlanSelectPermissions(): void {
    if (!this.billingSettings) {
      return;
    }

    const isFreemium = this.billingSettings.planId === "freemium";
    const currentPlan = this.billingSettings.planName ?? "";

    if (isFreemium && !this.auth.isSuperAdmin) {
      this.isPlanSelectDisabled = true;
      this.form.get("planName")?.disable();
      this.availablePlans = [currentPlan];
    } else {
      this.isPlanSelectDisabled = false;
      this.form.get("planName")?.enable();

      if (!isFreemium) {
        this.availablePlans = [...this.plans];
      } else {
        this.availablePlans = [...this.plans];
        if (currentPlan && !this.availablePlans.includes(currentPlan)) {
          this.availablePlans.unshift(currentPlan);
        }
      }
    }
  }

  runStripeCron(): void {
    this.isRunningStripeCron = true;
    this.billingSettingsService.runStripeCron().subscribe({
      next: () => {
        this.toastr.success(
          this.translate.instant(
            "settings.billing.stripeCronSuccess",
          ) as string,
        );
        this.isRunningStripeCron = false;
        this.refreshBillingForm();
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("settings.billing.stripeCronError") as string,
        );
        this.isRunningStripeCron = false;
      },
    });
  }

  hasFailedPayments(): boolean {
    if (!this.stats?.aggregated) {
      return false;
    }
    return this.stats.aggregated.some((stat) => stat.paymentFailed === true);
  }

  getDisplayedColumns(): string[] {
    return this.hasFailedPayments()
      ? this.displayedColumnsWithAction
      : this.displayedColumns;
  }

  retryFailedPayment(stripeLogId: string): void {
    if (!stripeLogId) {
      this.toastr.error(
        this.translate.instant("settings.billing.paymentRetryError") as string,
      );
      return;
    }

    this.billingSettingsService
      .createFailedPaymentCheckoutSession(stripeLogId)
      .subscribe({
        next: (response) => {
          window.location.href = response.url;
        },
        error: () => {
          this.toastr.error(
            this.translate.instant(
              "settings.billing.paymentRetryError",
            ) as string,
          );
        },
      });
  }

  cancelPendingPlanChange(): void {
    const dialogData: ConfirmationDialogParams = {
      title: this.translate.instant(
        "settings.billing.cancelPlanChangeTitle",
      ) as string,
      body: this.translate.instant(
        "settings.billing.cancelPlanChangeMessage",
      ) as string,
      confirmButton: this.translate.instant(
        "settings.billing.cancelPlanChange",
      ) as string,
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.cancellingPlanChange = true;

        this.billingSettingsService.cancelPendingPlanChange().subscribe({
          next: (response) => {
            this.billingSettings = response;
            this.toastr.success(
              this.translate.instant(
                "settings.billing.planChangeCancelled",
              ) as string,
            );
            this.cancellingPlanChange = false;
          },
          error: (error) => {
            console.error("Error cancelling plan change:", error);
            this.toastr.error(
              this.translate.instant(
                "settings.billing.planChangeCancelError",
              ) as string,
            );
            this.cancellingPlanChange = false;
          },
        });
      }
    });
  }

  isYearlyPlanWithExistingPayment(): boolean {
    const subscriptionPeriod = this.form.get("subscriptionPeriod")?.value as
      string | undefined;
    const isYearly = subscriptionPeriod === "yearly";
    const hasYearlyStats = this.stats?.yearly && this.stats.yearly.length > 0;
    return isYearly && hasYearlyStats;
  }

  openAdditionalLicensesDialog(): void {
    this.updateLocalBillingSettings();
    this.isProcessingPayment = true;
    this.showYearlyLicensePurchaseDialog();
  }

  isUpfrontPaymentWithActiveSubscription(): boolean {
    return (
      this.billingSettings?.billingMode === "upfront" &&
      !!this.billingSettings?.upfrontPayment?.subscriptionId
    );
  }

  isUpfrontPaymentPastDue(): boolean {
    return (
      this.billingSettings?.billingMode === "upfront" &&
      this.billingSettings?.upfrontPayment?.subscriptionStatus === "past_due" &&
      this.billingSettings?.planId === "free"
    );
  }

  isUpfrontPayment(): boolean {
    return this.billingSettings?.billingMode === "upfront";
  }

  isUpfrontPaymentWithoutSubscription(): boolean {
    return (
      this.billingSettings?.billingMode === "upfront" &&
      !this.billingSettings?.upfrontPayment?.subscriptionId
    );
  }

  upfrontLicenseCount: number = 0;
  upfrontCalculatedPrice: number = 0;
  upfrontCalculatedPriceWithVat: number = 0;
  upfrontVatAmount: number = 0;

  get upfrontMinLicenseCount(): number {
    return 10;
  }

  get upfrontHasLicenseCountError(): boolean {
    return this.upfrontLicenseCount < this.upfrontMinLicenseCount;
  }

  get upfrontHasVat(): boolean {
    const vatRate =
      (this.form.get("vatRate")?.value as number | undefined) ??
      this.billingSettings?.vatRate ??
      0;
    return vatRate > 0;
  }

  calculateUpfrontPrice(): void {
    const isLegacyBilling = this.billingSettings?.billingMode === "legacy";
    const effectiveEmployeeCount = isLegacyBilling
      ? this.upfrontLicenseCount
      : Math.max(this.upfrontLicenseCount, this.upfrontMinLicenseCount);
    const basePrice =
      (this.form.get("price")?.value as number | undefined) ||
      this.billingSettings?.price ||
      0;
    const subscriptionPeriod = this.getSubscriptionPeriodForUpfront();
    const multiplier = subscriptionPeriod === "yearly" ? 12 : 1;
    this.upfrontCalculatedPrice = parseFloat(
      (basePrice * effectiveEmployeeCount * multiplier).toFixed(2),
    );

    const vatRate =
      (this.form.get("vatRate")?.value as number | undefined) ??
      this.billingSettings?.vatRate ??
      0;
    if (vatRate > 0) {
      this.upfrontVatAmount = parseFloat(
        (this.upfrontCalculatedPrice * (vatRate / 100)).toFixed(2),
      );
      this.upfrontCalculatedPriceWithVat = parseFloat(
        (this.upfrontCalculatedPrice + this.upfrontVatAmount).toFixed(2),
      );
    } else {
      this.upfrontVatAmount = 0;
      this.upfrontCalculatedPriceWithVat = this.upfrontCalculatedPrice;
    }
  }

  public getSubscriptionPeriodForUpfront(): string {
    const formPeriod = this.form.get("subscriptionPeriod")?.value as
      string | undefined;
    if (formPeriod) {
      return formPeriod;
    }

    const settingsPeriod = this.billingSettings?.subscriptionPeriod;
    if (settingsPeriod) {
      return settingsPeriod;
    }

    const upfrontInterval = this.billingSettings?.upfrontPayment?.interval;
    if (upfrontInterval === "year") {
      return "yearly";
    }
    if (upfrontInterval === "month") {
      return "monthly";
    }

    return "monthly";
  }

  getUpfrontPricePerLicense(): number {
    const basePrice =
      (this.form.get("price")?.value as number | undefined) ||
      this.billingSettings?.price ||
      0;
    const period = this.getSubscriptionPeriodForUpfront();
    const raw = period === "yearly" ? basePrice * 12 : basePrice;
    return parseFloat(raw.toFixed(2));
  }

  getNextBillingCycleDate(): Date | null {
    if (!this.billingSettings?.upfrontPayment?.subscriptionStart) {
      return null;
    }

    const subscriptionStart = new Date(
      this.billingSettings.upfrontPayment.subscriptionStart,
    );
    const interval = this.billingSettings.upfrontPayment.interval || "month";
    const subscriptionPeriod = interval === "year" ? "yearly" : "monthly";

    if (subscriptionPeriod === "yearly") {
      const nextDate = new Date(subscriptionStart);
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      while (nextDate <= new Date()) {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
      return nextDate;
    } else {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      const startDate = new Date(subscriptionStart);
      const startDay = startDate.getDate();

      let nextDate = new Date(year, month, startDay);
      if (nextDate <= today) {
        nextDate = new Date(year, month + 1, startDay);
      }
      return nextDate;
    }
  }

  openChangeUpfrontLicensesDialog(): void {
    this.updateLocalBillingSettings();

    const currentLicenses =
      this.billingSettings?.upfrontPayment?.licenseCount || 0;
    const pendingLicenses =
      this.billingSettings?.upfrontPayment?.pendingLicenseCount;
    const pendingPrice = this.billingSettings?.upfrontPayment?.pendingPrice;
    const stripePrice = this.billingSettings?.upfrontPayment?.price;
    const stripeCurrency = this.billingSettings?.upfrontPayment?.currency;
    const billingPrice =
      (this.form.get("price")?.value as number | undefined) ||
      this.billingSettings?.price ||
      0;
    const billingCurrency =
      (this.form.get("currency")?.value as string | undefined) ||
      this.billingSettings?.currency ||
      "USD";

    const billingPeriod =
      (this.form.get("subscriptionPeriod")?.value as string | undefined) ||
      this.billingSettings?.subscriptionPeriod;
    const upfrontInterval = this.billingSettings?.upfrontPayment?.interval;
    const upfrontPeriod =
      upfrontInterval === "year"
        ? "yearly"
        : upfrontInterval === "month"
          ? "monthly"
          : null;
    const hasPeriodDifference =
      billingPeriod && upfrontPeriod && billingPeriod !== upfrontPeriod;
    const nextBillingCycleDate = this.getNextBillingCycleDate();

    const dialogData: UpfrontLicenseChangeDialogData = {
      currentLicenseCount: currentLicenses,
      pendingLicenseCount: pendingLicenses,
      currency: billingCurrency,
      planName:
        (this.form.get("planName")?.value as string | undefined) ||
        this.billingSettings?.planName ||
        "basic",
      price: billingPrice,
      pendingPrice,
      stripePrice,
      stripeCurrency,
      subscriptionPeriod: this.getSubscriptionPeriodForUpfront(),
      billingMode: this.billingSettings?.billingMode,
      vatRate:
        (this.form.get("vatRate")?.value as number | undefined) ??
        this.billingSettings?.vatRate ??
        null,
      hasPeriodDifference,
      currentPeriod: upfrontPeriod,
      newPeriod: billingPeriod,
      nextBillingCycleDate,
      hasSubdomains:
        (Array.isArray(this.billingSettings?.subdomains) &&
          this.billingSettings.subdomains.length > 0) ||
        !!this.auth.appUser?.subdomain ||
        this.billingSettings?.totalCurrentUserCount != null,
    };

    const dialogRef = this.dialog.open(UpfrontLicenseChangeDialogComponent, {
      width: "600px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: UpfrontLicenseChangeDialogResult | undefined) => {
        if (result && result.proceed) {
          const currentBillingPrice =
            (this.form.get("price")?.value as number | undefined) ||
            this.billingSettings?.price ||
            0;
          const currentBillingCurrency =
            (this.form.get("currency")?.value as string | undefined) ||
            this.billingSettings?.currency ||
            "USD";

          const hasLicenseChange =
            result.newLicenseCount !== undefined &&
            result.newLicenseCount !== currentLicenses;
          const shouldSyncPrice = result.syncPrice ?? false;
          const effectivePeriodDifference =
            result.hasPeriodDifference !== undefined
              ? result.hasPeriodDifference
              : billingPeriod &&
                upfrontPeriod &&
                billingPeriod !== upfrontPeriod;

          if (
            hasLicenseChange ||
            shouldSyncPrice ||
            effectivePeriodDifference
          ) {
            this.isProcessingPayment = true;
            const changes: Record<string, unknown> = {};

            if (hasLicenseChange || effectivePeriodDifference) {
              changes.newLicenseCount = result.newLicenseCount;
            }

            if (shouldSyncPrice) {
              changes.newPrice = currentBillingPrice;
            }

            if (result.isIncrease && hasLicenseChange) {
              const licenseCount = result.newLicenseCount ?? 0;
              this.saveBillingSettingsSilent().subscribe({
                next: () => {
                  this.billingSettingsService
                    .increaseUpfrontLicenses(licenseCount)
                    .subscribe({
                      next: (response) => {
                        window.location.href = response.url;
                      },
                      error: () => {
                        this.toastr.error(
                          this.translate.instant(
                            "settings.billing.paymentError",
                          ) as string,
                        );
                        this.isProcessingPayment = false;
                      },
                    });
                },
                error: () => {
                  this.toastr.error(
                    this.translate.instant(
                      "settings.billing.updateError",
                    ) as string,
                  );
                  this.isProcessingPayment = false;
                },
              });
            } else if (
              hasLicenseChange ||
              shouldSyncPrice ||
              effectivePeriodDifference
            ) {
              this.billingSettingsService
                .scheduleSubscriptionChanges(changes)
                .subscribe({
                  next: () => {
                    const changesList: string[] = [];
                    const newLicenseCount = result.newLicenseCount ?? 0;
                    if (result.isDecrease && hasLicenseChange) {
                      changesList.push(
                        this.translate.instant(
                          "settings.billing.licensesReduction",
                          { count: currentLicenses - newLicenseCount },
                        ) as string,
                      );
                    }
                    if (hasLicenseChange && !result.isDecrease) {
                      changesList.push(
                        this.translate.instant(
                          "settings.billing.licensesIncrease",
                          { count: newLicenseCount - currentLicenses },
                        ) as string,
                      );
                    }
                    if (shouldSyncPrice) {
                      changesList.push(
                        this.translate.instant("settings.billing.priceSyncTo", {
                          price: currentBillingPrice,
                          currency: currentBillingCurrency,
                        }) as string,
                      );
                    }
                    if (effectivePeriodDifference) {
                      const periodLabel =
                        billingPeriod === "yearly"
                          ? (this.translate.instant(
                              "settings.billing.yearly",
                            ) as string)
                          : (this.translate.instant(
                              "settings.billing.monthly",
                            ) as string);
                      changesList.push(
                        this.translate.instant(
                          "settings.billing.subscriptionPeriodChangeTo",
                          { period: periodLabel },
                        ) as string,
                      );
                    }
                    const message = this.translate.instant(
                      "settings.billing.successfullyScheduledChanges",
                      {
                        changes: changesList.join(", "),
                      },
                    ) as string;
                    this.toastr.success(message);
                    this.isProcessingPayment = false;
                    this.refreshBillingForm();
                  },
                  error: () => {
                    this.toastr.error(
                      this.translate.instant(
                        "settings.billing.paymentError",
                      ) as string,
                    );
                    this.isProcessingPayment = false;
                  },
                });
            } else {
              this.isProcessingPayment = false;
            }
          } else {
            this.isProcessingPayment = false;
          }
        } else {
          this.isProcessingPayment = false;
        }
      });
  }

  cancelPendingLicenseChange(): void {
    this.updateLocalBillingSettings();

    const dialogData: ConfirmationDialogParams = {
      title:
        (this.translate.instant(
          "settings.billing.cancelPendingChangeTitle",
        ) as string) || "Cancel Pending Change",
      body:
        (this.translate.instant(
          "settings.billing.cancelPendingChangeMessage",
        ) as string) ||
        "Are you sure you want to cancel the pending license change?",
      confirmButton:
        (this.translate.instant("settings.billing.confirm") as string) ||
        "Confirm",
    };
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.isProcessingPayment = true;
        this.billingSettingsService.cancelPendingLicenseChange().subscribe({
          next: () => {
            this.toastr.success(
              (this.translate.instant(
                "settings.billing.pendingChangeCancelled",
              ) as string) || "Pending license change cancelled successfully.",
            );
            this.isProcessingPayment = false;
            this.refreshBillingForm();
          },
          error: () => {
            this.toastr.error(
              this.translate.instant("settings.billing.paymentError") as string,
            );
            this.isProcessingPayment = false;
          },
        });
      }
    });
  }

  private showUpfrontLicensePurchaseDialog(): void {
    const rawCount =
      this.billingSettings?.totalCurrentUserCount ??
      (this.form.get("currentUserCount")?.value as number | undefined) ??
      this.billingSettings?.currentUserCount ??
      this.billingSettings?.upfrontPayment?.licenseCount ??
      0;
    const isLegacy = this.billingSettings?.billingMode === "legacy";
    const defaultEmployeeCount = isLegacy ? rawCount : Math.max(rawCount, 10);

    const dialogData: UpfrontLicensePurchaseDialogData = {
      defaultEmployeeCount,
      currency:
        (this.form.get("currency")?.value as string | undefined) ||
        this.billingSettings?.currency ||
        "USD",
      planName:
        (this.form.get("planName")?.value as string | undefined) ||
        this.billingSettings?.planName ||
        "basic",
      price:
        (this.form.get("price")?.value as number | undefined) ||
        this.billingSettings?.price ||
        0,
      subscriptionPeriod: this.getSubscriptionPeriodForUpfront(),
      billingMode: this.billingSettings?.billingMode,
      vatRate:
        (this.form.get("vatRate")?.value as number | undefined) ??
        this.billingSettings?.vatRate ??
        null,
      hasSubdomains:
        (Array.isArray(this.billingSettings?.subdomains) &&
          this.billingSettings.subdomains.length > 0) ||
        !!this.auth.appUser?.subdomain ||
        this.billingSettings?.totalCurrentUserCount != null,
    };

    const dialogRef = this.dialog.open(UpfrontLicensePurchaseDialogComponent, {
      width: "600px",
      disableClose: true,
      autoFocus: false,
      panelClass: "app-dialog-panel",
      data: dialogData,
    });

    dialogRef
      .afterClosed()
      .subscribe((result: LicensePurchaseDialogResult | undefined) => {
        if (
          result &&
          result.proceed &&
          result.numberOfEmployees !== undefined
        ) {
          this.billingSettingsService.validateBillingInfo().subscribe({
            next: (resp) => {
              if (resp.success) {
                this.proceedToUpfrontCheckout(result.numberOfEmployees);
              } else {
                this.handleValidationError(resp.missingFields || []);
              }
            },
            error: () => {
              this.toastr.error(
                this.translate.instant(
                  "settings.billing.validationError",
                ) as string,
              );
              this.isProcessingPayment = false;
            },
          });
        } else {
          this.isProcessingPayment = false;
        }
      });
  }

  private proceedToUpfrontCheckout(numberOfEmployees: number): void {
    const subscriptionPeriod = this.getSubscriptionPeriodForUpfront();
    this.saveBillingSettingsSilent().subscribe({
      next: () => {
        this.billingSettingsService
          .createUpfrontCheckoutSession(numberOfEmployees, subscriptionPeriod)
          .subscribe({
            next: (stripe) => (window.location.href = stripe.url),
            error: () => {
              this.toastr.error(
                this.translate.instant(
                  "settings.billing.paymentError",
                ) as string,
              );
              this.isProcessingPayment = false;
            },
          });
      },
      error: () => {
        this.toastr.error(
          this.translate.instant("settings.billing.updateError") as string,
        );
        this.isProcessingPayment = false;
      },
    });
  }
}
